import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/toaster";
import AsistenteFlotante from "./components/AsistenteFlotante";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";

// Public Pages
import HomePage from "./pages/HomePage";
import NosotrosPage from "./pages/NosotrosPage";
import BeneficiosPage from "./pages/BeneficiosPage";
import BeneficioDetallePage from "./pages/BeneficioDetallePage";
import AfiliacionPage from "./pages/AfiliacionPage";
import CalendarioPage from "./pages/CalendarioPage";
import ProveedoresPage from "./pages/ProveedoresPage";
import AcademiaPage from "./pages/AcademiaPage";
import CertificacionesPage from "./pages/CertificacionesPage";
import WebinarsPage from "./pages/WebinarsPage";
import WebinarWaitingRoom from "./pages/WebinarWaitingRoom";
import WebinarPaymentSuccess from "./pages/WebinarPaymentSuccess";
import WebinarAccessPage from "./pages/WebinarAccessPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import ProviderPortalPage from "./pages/ProviderPortalPage";
import RecursosPage from "./pages/RecursosPage";
import ContactoPage from "./pages/ContactoPage";

// Auth Pages
import LoginPage from "./pages/LoginPage";
import RegistroPage from "./pages/RegistroPage";
import RegistroInstitucionalPage from "./pages/RegistroInstitucionalPage";

// Institutional User Pages
import InvitadoDashboardPage from "./pages/InvitadoDashboardPage";

// CRM Pages
import CRMDashboard from "./pages/crm/CRMDashboard";
import UPIMIAPage from "./pages/UPIMIAPage";
import UpimIAChatPage from "./pages/UpimIAChatPage";

// Institutional Pages
import DirectorioPublicoPage from "./pages/DirectorioPublicoPage";
import AsesoriasEspecializadasPage from "./pages/AsesoriasEspecializadasPage";
import PostulacionLiderazgoPage from "./pages/PostulacionLiderazgoPage";
import AdminInstitucionalPage from "./pages/admin/AdminInstitucionalPage";

// CRM Additional Pages
import CRMPropertiesPage from "./pages/crm/CRMPropertiesPage";
import PropertyFormPage from "./pages/crm/PropertyFormPage";
import AlliancesPage from "./pages/crm/AlliancesPage";
import MicrositePage from "./pages/crm/MicrositePage";
import CustomerBotPage from "./pages/crm/CustomerBotPage";
import PublicMicrositePage from "./pages/PublicMicrositePage";
import DigitalCredentialPage from "./pages/crm/DigitalCredentialPage";
import MarketReportsPage from "./pages/crm/MarketReportsPage";
import PropertyAlertsPage from "./pages/crm/PropertyAlertsPage";
import CRMContactsPage from "./pages/crm/CRMContactsPage";
import CRMPipelinePage from "./pages/crm/CRMPipelinePage";
import CRMActivitiesPage from "./pages/crm/CRMActivitiesPage";

// Legal Pages
import AvisoPrivacidadPage from "./pages/AvisoPrivacidadPage";
import TerminosPage from "./pages/TerminosPage";

// Dashboard Pages
import DashboardHomePage from "./pages/dashboard/DashboardHomePage";
import CredencialPage from "./pages/dashboard/CredencialPage";
import MembresiaPage from "./pages/dashboard/MembresiaPage";
import MensajesPage from "./pages/dashboard/MensajesPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAfiliados from "./pages/admin/AdminAfiliados";
import AdminCarruseles from "./pages/admin/AdminCarruseles";
import AdminMensajes from "./pages/admin/AdminMensajes";
import AdminEventos from "./pages/admin/AdminEventos";
import AdminAfiliaciones from "./pages/admin/AdminAfiliaciones";
import AdminRenovaciones from "./pages/admin/AdminRenovaciones";
import AdminCRMTerritorial from "./pages/admin/AdminCRMTerritorial";
import CuentaSeguridadPage from "./pages/CuentaSeguridadPage";
import { AfiliacionPagoExitoPage, AfiliacionPagoCanceladoPage } from "./pages/AfiliacionPagoPages";
import AdminProveedores from "./pages/admin/AdminProveedores";
import AdminCheckin from "./pages/admin/AdminCheckin";
import AdminWebinars from "./pages/admin/AdminWebinars";
import AdminCertificaciones from "./pages/admin/AdminCertificaciones";
import AdminCupones from "./pages/admin/AdminCupones";
import AdminNotificaciones from "./pages/admin/AdminNotificaciones";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminCRM from "./pages/admin/AdminCRM";
import AdminVerificaciones from "./pages/admin/AdminVerificaciones";
import WhatsAppAdminPage from "./pages/admin/WhatsAppAdminPage";
import AdminUpimIA from "./pages/admin/AdminUpimIA";
import AdminOmnichannel from "./pages/admin/AdminOmnichannel";
import AdminGovernance from "./pages/admin/AdminGovernance";
import AdminOrganizacion from "./pages/admin/AdminOrganizacion";
import AfiliacionSolicitudPage from "./pages/AfiliacionSolicitudPage";
import AfiliacionRetomarPage from "./pages/AfiliacionRetomarPage";
import AfiliacionRedirect from "./pages/AfiliacionRedirect";
import AdminVideos from "./pages/admin/AdminVideos";
import AdminLives from "./pages/admin/AdminLives";
import AdminPonentes from "./pages/admin/AdminPonentes";
import AdminMaterials from "./pages/admin/AdminMaterials";
import AdminEducationalContent from "./pages/admin/AdminEducationalContent";
import AdminEmailMarketing from "./pages/admin/AdminEmailMarketing";
import AdminBiblioteca from "./pages/admin/AdminBiblioteca";
import AdminQuizzes from "./pages/admin/AdminQuizzes";
import AdminRecognitions from "./pages/admin/AdminRecognitions";
import AdminBadgesPage from "./pages/admin/AdminBadgesPage";
import VerifyConstanciaPage from "./pages/VerifyConstanciaPage";
import VerifyRecognitionPage from "./pages/VerifyRecognitionPage";
import MyConstanciasPage from "./pages/MyConstanciasPage";
import PublicPortfolioPage from "./pages/PublicPortfolioPage";
import ShareableBadges from "./pages/ShareableBadges";

// Public Educational Pages
import MasterclassesPage from "./pages/MasterclassesPage";
import ContentDetailPage from "./pages/ContentDetailPage";
import SpeakerDashboard from "./pages/SpeakerDashboard";
import BibliotecaPage from "./pages/BibliotecaPage";
import VerifyCertificatePage from "./pages/VerifyCertificatePage";

// Utility Pages
import NotFoundPage from "./pages/NotFoundPage";
import PerfilFiscalPage from "./pages/PerfilFiscalPage";
import BandejaFacturacionPage from "./pages/admin/BandejaFacturacionPage";

// App Router - Detects session_id during render (NOT in useEffect)
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - synchronous to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return <AppRoutes />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />      <Route path="/nosotros" element={<NosotrosPage />} />
      <Route path="/beneficios" element={<BeneficiosPage />} />
      <Route path="/beneficios/:beneficioId" element={<BeneficioDetallePage />} />
      <Route path="/afiliacion" element={<AfiliacionRedirect />} />
      <Route path="/afiliacion/planes" element={<AfiliacionPage />} />
      <Route path="/calendario" element={<CalendarioPage />} />
      <Route path="/proveedores" element={<ProveedoresPage />} />
      <Route path="/academia" element={<AcademiaPage />} />
      <Route path="/certificaciones" element={<CertificacionesPage />} />
      <Route path="/webinars" element={<WebinarsPage />} />
      <Route path="/webinars/:webinarId/sala" element={<WebinarWaitingRoom />} />
      <Route path="/webinars/:webinarId/pago-exitoso" element={<ProtectedRoute><WebinarPaymentSuccess /></ProtectedRoute>} />
      <Route path="/webinars/:webinarId/acceso" element={<WebinarAccessPage />} />
      <Route path="/pago-exitoso" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/mis-datos-fiscales" element={<ProtectedRoute><PerfilFiscalPage /></ProtectedRoute>} />
      <Route path="/facturacion/solicitudes" element={<ProtectedRoute><BandejaFacturacionPage /></ProtectedRoute>} />
      <Route path="/facturacion/solicitudes/:solicitudId" element={<ProtectedRoute><BandejaFacturacionPage /></ProtectedRoute>} />
      <Route path="/portal-proveedores" element={<ProviderPortalPage />} />
      <Route path="/recursos" element={<RecursosPage />} />
      <Route path="/contacto" element={<ContactoPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registro" element={<RegistroPage />} />
      <Route path="/registro-institucional" element={<RegistroInstitucionalPage />} />
      
      {/* Master Classes / Educational Content */}
      <Route path="/masterclasses" element={<MasterclassesPage />} />
      <Route path="/masterclasses/:slug" element={<ContentDetailPage />} />
      <Route path="/portal-ponente" element={<SpeakerDashboard />} />
      <Route path="/biblioteca" element={<BibliotecaPage />} />
      <Route path="/verificar-certificado/:code" element={<VerifyCertificatePage />} />
      
      {/* Institutional User (Invitado) Dashboard */}
      <Route path="/dashboard/invitado" element={
        <ProtectedRoute><InvitadoDashboardPage /></ProtectedRoute>
      } />
      
      {/* CRM Routes - Specific routes first */}
      <Route path="/crm/propiedades" element={<ProtectedRoute><CRMPropertiesPage /></ProtectedRoute>} />
      <Route path="/crm/propiedades/nueva" element={<ProtectedRoute><PropertyFormPage /></ProtectedRoute>} />
      <Route path="/crm/propiedades/:id" element={<ProtectedRoute><PropertyFormPage /></ProtectedRoute>} />
      <Route path="/crm/contacts" element={<ProtectedRoute><CRMContactsPage /></ProtectedRoute>} />
      <Route path="/crm/pipeline" element={<ProtectedRoute><CRMPipelinePage /></ProtectedRoute>} />
      <Route path="/crm/activities" element={<ProtectedRoute><CRMActivitiesPage /></ProtectedRoute>} />
      <Route path="/crm/alianzas" element={<ProtectedRoute><AlliancesPage /></ProtectedRoute>} />
      <Route path="/crm/micrositio" element={<ProtectedRoute><MicrositePage /></ProtectedRoute>} />
      <Route path="/crm/bot-clientes" element={<ProtectedRoute><CustomerBotPage /></ProtectedRoute>} />
      <Route path="/crm/credencial-digital" element={<ProtectedRoute><DigitalCredentialPage /></ProtectedRoute>} />
      <Route path="/crm/reportes-mercado" element={<ProtectedRoute><MarketReportsPage /></ProtectedRoute>} />
      <Route path="/crm/alertas" element={<ProtectedRoute><PropertyAlertsPage /></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute><CRMDashboard /></ProtectedRoute>} />
      
      {/* Public Agent Microsite */}
      <Route path="/asesor/:slug" element={<PublicMicrositePage />} />
      
      <Route path="/upim-ia" element={<UPIMIAPage />} />
      <Route path="/upim-ia/chat" element={<ProtectedRoute><UpimIAChatPage /></ProtectedRoute>} />
      
      {/* Institutional Routes */}
      <Route path="/directorio" element={<DirectorioPublicoPage />} />
      <Route path="/asesorias" element={<AsesoriasEspecializadasPage />} />
      <Route path="/postulacion-liderazgo" element={<ProtectedRoute><PostulacionLiderazgoPage /></ProtectedRoute>} />
      <Route path="/admin/institucional" element={<ProtectedRoute><AdminInstitucionalPage /></ProtectedRoute>} />
      
      {/* Legal Routes */}
      <Route path="/aviso-privacidad" element={<AvisoPrivacidadPage />} />
      <Route path="/terminos" element={<TerminosPage />} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardHomePage /></ProtectedRoute>
      } />
      <Route path="/dashboard/credencial" element={
        <ProtectedRoute><CredencialPage /></ProtectedRoute>
      } />
      <Route path="/dashboard/membresia" element={
        <ProtectedRoute><MembresiaPage /></ProtectedRoute>
      } />
      <Route path="/dashboard/mensajes" element={
        <ProtectedRoute><MensajesPage /></ProtectedRoute>
      } />
      <Route path="/dashboard/eventos" element={
        <ProtectedRoute><DashboardHomePage /></ProtectedRoute>
      } />
      <Route path="/dashboard/beneficios" element={
        <ProtectedRoute><DashboardHomePage /></ProtectedRoute>
      } />
      <Route path="/dashboard/academia" element={
        <ProtectedRoute><DashboardHomePage /></ProtectedRoute>
      } />
      <Route path="/dashboard/documentos" element={
        <ProtectedRoute><DashboardHomePage /></ProtectedRoute>
      } />
      <Route path="/dashboard/perfil" element={
        <ProtectedRoute><DashboardHomePage /></ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<NotFoundPage />} />
      
      {/* Recognition Routes */}
      <Route path="/verificar-constancia/:code" element={<VerifyConstanciaPage />} />
      <Route path="/verificar-reconocimiento/:code" element={<VerifyRecognitionPage />} />
      <Route path="/mis-constancias" element={<MyConstanciasPage />} />
      <Route path="/mis-insignias" element={<ShareableBadges />} />
      <Route path="/portafolio/:affiliationNumber" element={<PublicPortfolioPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/afiliados" element={<AdminAfiliados />} />
      <Route path="/admin/carruseles" element={<AdminCarruseles />} />
      <Route path="/admin/mensajes" element={<AdminMensajes />} />
      <Route path="/admin/proveedores" element={<AdminProveedores />} />
      <Route path="/admin/eventos" element={<AdminEventos />} />
      <Route path="/admin/afiliaciones" element={<AdminAfiliaciones />} />
      <Route path="/admin/renovaciones" element={<AdminRenovaciones />} />
      <Route path="/admin/crm-territorial" element={<AdminCRMTerritorial />} />
      <Route path="/cuenta/seguridad" element={<ProtectedRoute><CuentaSeguridadPage /></ProtectedRoute>} />
      <Route path="/afiliacion/pago/exito" element={<AfiliacionPagoExitoPage />} />
      <Route path="/afiliacion/pago/cancelado" element={<AfiliacionPagoCanceladoPage />} />
      <Route path="/admin/checkin" element={<AdminCheckin />} />
      <Route path="/admin/webinars" element={<AdminWebinars />} />
      <Route path="/admin/certificaciones" element={<AdminCertificaciones />} />
      <Route path="/admin/cupones" element={<AdminCupones />} />
      <Route path="/admin/notificaciones" element={<AdminNotificaciones />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/admin/crm" element={<AdminCRM />} />
      <Route path="/admin/verificaciones" element={<AdminVerificaciones />} />
      <Route path="/admin/upim-ia" element={<AdminUpimIA />} />
      <Route path="/admin/omnichannel" element={<AdminOmnichannel />} />
      <Route path="/admin/governance" element={<AdminGovernance />} />
      <Route path="/admin/nacional" element={<AdminOrganizacion />} />
      <Route path="/afiliacion/solicitud" element={<AfiliacionSolicitudPage />} />
      <Route path="/afiliacion/retomar/:token" element={<AfiliacionRetomarPage />} />
      <Route path="/admin/videos" element={<AdminVideos />} />
      <Route path="/admin/lives" element={<AdminLives />} />
      <Route path="/admin/ponentes" element={<AdminPonentes />} />
      <Route path="/admin/materiales" element={<AdminMaterials />} />
      <Route path="/admin/academia" element={<AdminEducationalContent />} />
      <Route path="/admin/email-marketing" element={<AdminEmailMarketing />} />
      <Route path="/admin/biblioteca" element={<AdminBiblioteca />} />
      <Route path="/admin/examenes" element={<AdminQuizzes />} />
      <Route path="/admin/reconocimientos" element={<AdminRecognitions />} />
      <Route path="/admin/badges" element={<AdminBadgesPage />} />
      <Route path="/admin/whatsapp" element={<WhatsAppAdminPage />} />
      <Route path="/admin/pagos" element={<AdminDashboard />} />
      <Route path="/admin/contenido" element={<AdminDashboard />} />
      <Route path="/admin/configuracion" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
          <AsistenteFlotante />
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;

